/**
 * The thickness the next wall is drawn with, and the last few this plan used.
 *
 * A thickness is a **drawing habit**, not a property of the plan: the walls
 * carry their own once drawn, so nothing here is ever written server-side.
 * `sessionStorage`, keyed by the frame asset, is exactly the right lifetime —
 * the habit survives a reload while one floor is being traced, and does not
 * follow the user to the next floor or into tomorrow's session.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { DEFAULT_WALL_THICKNESS } from './engine/openings'

/** How many distinct values the plan keeps within reach. */
export const THICKNESS_MEMORY = 5

const storageKey = (assetUid: string): string => `plan-editor:wall-thickness:${assetUid}`

/** Most-recent first, distinct, capped — the oldest value falls off the end. */
export function rememberThickness(recent: number[], mm: number): number[] {
    return [mm, ...recent.filter((v) => v !== mm)].slice(0, THICKNESS_MEMORY)
}

const usable = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0

function storedRecent(assetUid: string): number[] {
    try {
        const raw = sessionStorage.getItem(storageKey(assetUid))
        const parsed: unknown = raw ? JSON.parse(raw) : null
        if (!Array.isArray(parsed)) return [DEFAULT_WALL_THICKNESS]
        const values = parsed.filter(usable).slice(0, THICKNESS_MEMORY)
        return values.length ? values : [DEFAULT_WALL_THICKNESS]
    } catch {
        // Unavailable or unparseable storage: the default is a fine start.
        return [DEFAULT_WALL_THICKNESS]
    }
}

export interface WallThicknessMemory {
    /** The value the next wall takes — the last one entered. */
    thickness: number
    /** That value first, then up to four earlier ones. */
    recent: number[]
    remember: (mm: number) => void
}

export function useWallThickness(assetUid: string): WallThicknessMemory {
    const [recent, setRecent] = useState<number[]>(() => storedRecent(assetUid))

    // The route reuses this component across frames (`/…/{uid}/plan-editor` →
    // another uid), so the memory has to follow the plan, not the mount.
    const loadedFor = useRef(assetUid)
    useEffect(() => {
        if (loadedFor.current === assetUid) return
        loadedFor.current = assetUid
        setRecent(storedRecent(assetUid))
    }, [assetUid])

    const remember = useCallback((mm: number) => {
        if (!usable(mm)) return
        setRecent((prev) => {
            const next = rememberThickness(prev, mm)
            try {
                sessionStorage.setItem(storageKey(assetUid), JSON.stringify(next))
            } catch {
                // Storage is a convenience; the value still applies to the draw.
            }
            return next
        })
    }, [assetUid])

    return { thickness: recent[0] ?? DEFAULT_WALL_THICKNESS, recent, remember }
}
